/**
 * Shadow-root mount utilities for federation remotes, shared by
 * apps/test-suite and apps/agent-tools (imported by relative path, like
 * ./shared.mjs). Bundled into each remote's own chunk — deliberately NOT a
 * federation shared module, so mount cannot break on host share-scope skew.
 *
 * Remotes render inside an open shadow root so their CSS (Tailwind preflight,
 * ui-lib tokens, app styles) cannot leak into the aistudio host and vice
 * versa. The contract this relies on:
 *
 * - The remote's federation build emits ONE stylesheet at a stable path
 *   (`build.cssCodeSplit: false` + an `assetFileNames` rename to `style.css`)
 *   which is linked into the shadow root, never `document.head`.
 * - ui-lib design tokens are declared on `:root, :host` (shared-theme.css) so
 *   they resolve both standalone and inside a shadow tree.
 * - `@font-face` rules are ignored inside shadow trees, so fonts must be
 *   registered at document level via `ensureDocumentFonts`.
 * - CSS `@property` rules are document-scoped by spec — Chrome ignores them
 *   inside shadow DOM stylesheets. The `federationPropertyRules` build plugin
 *   emits them into a sibling `properties.css`, which
 *   `ensureDocumentPropertyRules` links into `document.head`.
 * - Radix/shadcn portals default to `document.body` (outside the shadow
 *   root); callers must point ui-lib's `setEmbedPortalContainer` at the
 *   returned container.
 */

const FONT_CSS_URL = 'https://use.typekit.net/fir2ltl.css';

// Every remote shares the same font URL but supplies its own linkId, so
// deduping on linkId alone still lets N mounted remotes create N <link>
// elements for the identical stylesheet. Dedupe on the href too — first
// remote to mount wins, later ones just no-op.
//
// Future enhancement: FONT_CSS_URL is hardcoded rather than accepted as a
// parameter, so every remote is forced onto this one font/provider via
// shared tooling. Accepted for now since every federated app deliberately
// uses the same design-system font — revisit (ensureDocumentFonts({ href,
// linkId }), dedupe still keyed on href) if/when a remote needs a different
// one, or move font loading to the host so remotes stop duplicating it.
export function ensureDocumentFonts(linkId: string): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(linkId)) return;
  if (document.querySelector(`link[href="${FONT_CSS_URL}"]`)) return;
  const link = document.createElement('link');
  link.id = linkId;
  link.rel = 'stylesheet';
  link.href = FONT_CSS_URL;
  document.head.appendChild(link);
}

export type ShadowMountOptions = {
  /** URL of the remote's single CSS bundle, resolved by the caller relative to its own module (e.g. `new URL('../style.css', import.meta.url).href`). */
  styleHref: string;
  /** Tag name for the container element created inside the shadow root. Defaults to `div`. */
  containerTagName?: string;
};

export function createShadowMountContainer(
  el: HTMLElement,
  { styleHref, containerTagName = 'div' }: ShadowMountOptions
): HTMLElement {
  const shadow = el.shadowRoot ?? el.attachShadow({ mode: 'open' });
  shadow.replaceChildren();
  const style = document.createElement('link');
  style.rel = 'stylesheet';
  style.href = styleHref;
  shadow.appendChild(style);
  const container = document.createElement(containerTagName);
  container.style.display = 'contents';
  shadow.appendChild(container);
  return container;
}

/**
 * CSS `@property` rules are document-scoped by spec. Chrome ignores them when
 * they appear inside a shadow root's stylesheet. Tailwind v4 relies on
 * `@property` to set initial values for internal variables like
 * `--tw-border-style`, `--tw-translate-x`, etc. Without registration, those
 * variables resolve to nothing and utilities that depend on them break
 * (invisible borders, broken transforms, missing shadows).
 *
 * The `federationPropertyRules` build plugin extracts those rules into
 * `properties.css` next to the remote's `style.css`; this links that file into
 * `document.head`, where Chrome registers them globally. It holds only
 * registration metadata — no selectors — so nothing visual leaks into the
 * host, and linking it (rather than reading the CSSOM) keeps working when the
 * remote is served cross-origin.
 */
export function ensureDocumentPropertyRules(styleHref: string): void {
  if (typeof document === 'undefined') return;

  const href = styleHref.replace(/style\.css(?=$|[?#])/, 'properties.css');
  if (href === styleHref) return;

  const selector = `link[data-federation-properties="${href}"]`;
  if (document.head.querySelector(selector)) return;

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.dataset.federationProperties = href;
  link.href = href;
  document.head.appendChild(link);
}

/**
 * Resolves once the stylesheet link(s) inside `el`'s shadow root have loaded,
 * so callers can defer rendering (and the host can keep its loader up) until
 * the remote paints styled. Resolves on error or after `timeoutMs` too —
 * a broken CDN must degrade to unstyled content, not a permanent spinner.
 */
export function loadCSSFromLink(el: HTMLElement, timeoutMs = 5000): Promise<void> {
  // jsdom/happy-dom never load a real stylesheet, so every link stays
  // `sheet === null` and the wait below would burn the full timeout in tests.
  // Keyed off the build mode rather than sniffing `navigator.userAgent`: the
  // UA string isn't contractual, and a real browser matching it would silently
  // skip a wait the host depends on.
  if (import.meta.env?.MODE === 'test') return Promise.resolve();

  const links = Array.from(
    el.shadowRoot?.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]') ?? []
  ).filter((link) => !link.sheet);

  if (links.length === 0) return Promise.resolve();

  return new Promise((resolve) => {
    let remaining = links.length;
    const timer = setTimeout(() => resolve(), timeoutMs);
    const settle = () => {
      remaining -= 1;
      if (remaining === 0) {
        clearTimeout(timer);
        resolve();
      }
    };
    for (const link of links) {
      link.addEventListener('load', settle, { once: true });
      link.addEventListener('error', settle, { once: true });
    }
  });
}
